import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

// --- Type Definitions ---
export interface IBannerV1Item {
  Title: string;
  Subtitle: string;
  ImageUrl: string;
}

export interface IBannerV1Widget {
  Items: IBannerV1Item[];
}

// --- Props for the BannerV1Item component ---
interface BannerV1ItemProps {
  item: IBannerV1Widget;
  error: any;
  onChange: (updatedItem: IBannerV1Widget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const BannerV1Item: React.FC<BannerV1ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Ensure Items is an array.
  const bannerItems: IBannerV1Item[] = Array.isArray(item.Items) ? item.Items : [];

  // Local state to control the image picker.
  const [imagePickerIndex, setImagePickerIndex] = useState<number | null>(null);

  // Helper: Extract value from event or use string directly.
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // Handler for input field changes.
  const handleItemChange = (
    index: number,
    field: keyof IBannerV1Item,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedItems = [...bannerItems];
    updatedItems[index] = { ...updatedItems[index], [field]: newValue };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // Handler for selecting an image.
  const handleImageChange = (selected: string | string[]) => {
    const newImage = Array.isArray(selected) ? selected[0] : selected;
    if (imagePickerIndex !== null) {
      const updatedItems = [...bannerItems];
      updatedItems[imagePickerIndex] = {
        ...updatedItems[imagePickerIndex],
        ImageUrl: newImage,
      };
      onChange({ ...item, Items: updatedItems }, "Items");
    }
    setImagePickerIndex(null);
  };

  // Add a new banner item.
  const handleAddItem = () => {
    const newItem: IBannerV1Item = { Title: "", Subtitle: "", ImageUrl: "" };
    const updatedItems = [...bannerItems, newItem];
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // Remove a banner item.
  const handleRemoveItem = (index: number) => {
    const updatedItems = bannerItems.filter((_, i) => i !== index);
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Banner Items
        </Typography>

        {/* Banner Items Section */}
        {bannerItems.map((banner, index) => (
          <Box
            key={index}
            sx={{
              mb: 2,
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              {/* Title Field */}
              <Grid item xs={12} sm={4}>
                <GenericInput
                  type="text"
                  name={`Title-${index}`}
                  title="Title"
                  value={banner.Title}
                  error={error?.[index]?.Title || ""}
                  onChange={(e) => handleItemChange(index, "Title", e)}
                  onBlur={() => onblur("Title", index)}
                  disabled={false}
                />
              </Grid>

              {/* Subtitle Field */}
              <Grid item xs={12} sm={4}>
                <GenericInput
                  type="text"
                  name={`Subtitle-${index}`}
                  title="Subtitle"
                  value={banner.Subtitle}
                  error={error?.[index]?.Subtitle || ""}
                  onChange={(e) => handleItemChange(index, "Subtitle", e)}
                  onBlur={() => onblur("Subtitle", index)}
                  disabled={false}
                />
              </Grid>

              {/* Image Picker */}
              <Grid item xs={12} sm={3}>
                {banner.ImageUrl ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: 100,
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      overflow: "hidden",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f9f9f9",
                    }}
                    onClick={() => setImagePickerIndex(index)}
                  >
                    <img
                      src={banner.ImageUrl}
                      alt={`Banner ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setImagePickerIndex(index)}
                  >
                    Select Image
                  </Button>
                )}
                {error?.[index]?.ImageUrl && (
                  <Typography variant="body2" color="error">
                    {error[index].ImageUrl}
                  </Typography>
                )}
              </Grid>

              {/* Delete Button */}
              <Grid item xs={12} sm={1}>
                <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem}>
          Add Banner Item
        </Button>
      </CardContent>

      {/* Image Picker Dialog */}
      {imagePickerIndex !== null && (
        <ImagePicker
          open={true}
          multiSelect={false}
          onClose={() => setImagePickerIndex(null)}
          onSelect={handleImageChange}
        />
      )}
    </Card>
  );
};

export default BannerV1Item;
